// Values a table column can be ordered by. Numbers keep their own ordering;
// everything else is compared as text.
export type SortableValue = string | number;

export type ValueComparator = (a: SortableValue, b: SortableValue) => number;

// `numeric` keeps embedded digits in human order ("Room 2" before "Room 10")
// instead of the lexicographic order a plain code-point compare would give.
// `sensitivity: 'base'` ignores case and accents, so "anna" and "Anna" sit
// together. In Danish it still separates Æ, Ø and Å from A, E and O, because
// there they are their own base letters rather than accented forms.
const COLLATOR_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' };

// Builds a comparator that orders values the way a reader of `locale` expects.
//
// The locale is a correctness argument, not a polish one. Æ, Ø and Å close the
// Danish alphabet, and outside a Danish collation they scatter into the middle
// of the list in two different ways: Å and Ø fold into A and O, so "Åse" lands
// between "Anna" and "Bo", while Æ is not folded but misfiled, sorting as its
// own letter ahead of the whole A block, so "Ærø" comes before "Anna". The app
// pins this to Danish (see SORT_LOCALE); the parameter stays so the util itself
// is not tied to one language.
//
// Deliberately unguarded: its only app caller passes a hardcoded tag (SORT_LOCALE),
// so a tag malformed enough to make the constructor throw cannot reach here, and
// the app's other Intl call sites (dateUtils, currencyUtils) pass theirs in just
// as directly.
//
// Returns a comparator rather than comparing directly so one collator serves a
// whole sort; constructing one per comparison is the expensive way to do this.
export function createValueComparator(locale: string): ValueComparator {
  const collator = new Intl.Collator(locale, COLLATOR_OPTIONS);
  return (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return collator.compare(String(a), String(b));
  };
}
